import {useContext, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import {atomIconSizes} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import PrimitiveTypography, {
  primitiveTypographyDesign,
  primitiveTypographyFontSize,
  primitiveTypographyFontWeight
} from '@s-ui/react-primitive-typography'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import IconAiWand from '@adv-mt/mt-svg-icons/lib/AiWand.js'
import IconChevronDown from '@adv-mt/mt-svg-icons/lib/ChevronDown.js'
import IconChevronRight from '@adv-mt/mt-svg-icons/lib/ChevronRight.js'
import IconChevronUp from '@adv-mt/mt-svg-icons/lib/ChevronUp.js'

import SearchAiField from './components/SearchAiField.js'
import SearchAiQuickFiltersModalContent from './components/SearchAiQuickFiltersModalContent.js'
import SearchAiQuickFiltersModalFooter from './components/SearchAiQuickFiltersModalFooter.js'
import useAiSearch from './hooks/useAiSearch.js'
import {BASE_CLASS} from './config.js'

const SearchAiQuickFilters = ({
  isLoading = false,
  onSearch = () => {},
  isDefaultOpen = false,
  onShowResults = () => {},
  onOpenModal = () => {}
}) => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const isMobile = config.get('isMobile')
  const isTablet = config.get('isTablet')

  const isDesktopOrWider = useMediaQuery(useMediaQueryBreakpoints.lg, {
    defaultMatches: !isMobile && !isTablet
  })

  const [currentInputSearch, setCurrentInputSearch] = useState('')
  const [isOpen, setIsOpen] = useState(isDefaultOpen)

  const handleChangeInputSearch = search => {
    setCurrentInputSearch(search)
  }

  const handleInputSearch = async e => {
    e?.preventDefault()
    const cleanedInputSearch = currentInputSearch.trim()

    if (cleanedInputSearch) {
      await onSearch({query: cleanedInputSearch})
    }
  }

  const onClickCta = () => {
    setIsOpen(!isOpen)

    // On smaller screens handle the modal on the parent to avoid nested modals
    if (!isDesktopOrWider) {
      onOpenModal?.()
    }
  }

  const iconProps = {size: atomIconSizes.medium}
  const desktopRightIcon = isOpen ? <IconChevronUp {...iconProps} /> : <IconChevronDown {...iconProps} />
  const mobileRightIcon = <IconChevronRight {...iconProps} />

  return (
    <>
      <div className={BASE_CLASS}>
        <div data-testid="ai-quick-filters-cta" className={`${BASE_CLASS}-cta`} onClick={onClickCta}>
          <IconAiWand {...iconProps} />
          <PrimitiveTypography
            as="span"
            design={primitiveTypographyDesign.BODY_1}
            fontSize={primitiveTypographyFontSize.L}
            fontWeight={primitiveTypographyFontWeight.SEMI_BOLD}
          >
            {i18n.t('SEARCH_AI.FILTERS.LABEL')}
          </PrimitiveTypography>
          <div className={`${BASE_CLASS}-cta-rightIcon`}>{isDesktopOrWider ? desktopRightIcon : mobileRightIcon}</div>
        </div>

        {/* 
          On Desktop: show form below CTA
          On Mobile: use SearchAiQuickFiltersModalContent + SearchAiQuickFiltersModalFooter in a BasicModal on the parent to avoid nested modals 
        */}
        {isDesktopOrWider && isOpen && (
          <form className={`${BASE_CLASS}-content`} onSubmit={handleInputSearch}>
            <SearchAiField
              value={currentInputSearch}
              onChange={handleChangeInputSearch}
              onSubmit={handleInputSearch}
              isLoading={isLoading}
            />

            <AtomButton
              disabled={isLoading || !currentInputSearch}
              size={atomButtonSizes.medium}
              shape={atomButtonShapes.CIRCULAR}
            >
              {i18n.t('SEARCH_AI.FILTERS.CTA')}
            </AtomButton>
          </form>
        )}
      </div>
    </>
  )
}

SearchAiQuickFilters.displayName = 'SearchAiQuickFilters'
SearchAiQuickFilters.propTypes = {
  isLoading: PropTypes.bool,
  onSearch: PropTypes.func,
  isDefaultOpen: PropTypes.bool,
  onShowResults: PropTypes.func,
  onOpenModal: PropTypes.func
}

export {SearchAiQuickFilters as default, SearchAiQuickFiltersModalContent, SearchAiQuickFiltersModalFooter, useAiSearch}
